import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

export default function HeroSequence() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // 1. Background zooms IN (Scales up) to create an immersive feeling
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const bgBlur = useTransform(scrollYProgress, [0, 0.8], ["blur(0px)", "blur(4px)"]);
  const bgOpacity = useTransform(scrollYProgress, [0.7, 1], [0.6, 0.2]);

  // 2. Text zooms OUT (Scales down) and becomes clearer
  // Starts large and slightly transparent, ends perfectly sized and solid
  const textScale = useTransform(scrollYProgress, [0, 0.5], [1.8, 1]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.4], [0, 1]);
  const textY = useTransform(scrollYProgress, [0, 0.5], [100, 0]);

  // 3. Family fades out early to focus on the message
  const familyScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.3]);
  const familyOpacity = useTransform(scrollYProgress, [0, 0.4], [0.9, 0]);

  return (
    <section ref={containerRef} className="relative h-[300vh] bg-brand-light">
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
        {/* BACKGROUND LAYER - Zooms IN */}
        <motion.div 
          style={{ scale: bgScale, filter: bgBlur, opacity: bgOpacity }}
          className="absolute inset-0 z-0 origin-center"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#e8ecea] to-[#f8f8f6] opacity-60 z-10 mix-blend-overlay" />
          <img 
            src="https://media-manager-c.questera.ai/greta-media/84bd1a1ec4b169d3b45fb6d9222544ea571861834b2a41df59b8dd4fbd9877604e1a1a824c0c77323e9e6a3b81021929/images/aW1hZ2UvanBlZw==/6e8df4a2520241100e52aefb2ae661c2.jpg" 
            alt="Soft nature background" 
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* MIDDLE LAYER - TEXT & LOGO - Zooms OUT and clarifies */}
        <motion.div 
          style={{ scale: textScale, opacity: textOpacity, y: textY }}
          className="absolute z-20 flex flex-col items-center justify-center w-full pointer-events-none px-6"
        >
          {/* Logo size increased from h-16/24/28 to h-24/40/48 for a bigger first frame impact */}
          <img 
            src="https://media-manager-c.questera.ai/greta-media/84bd1a1ec4b169d3b45fb6d9222544ea571861834b2a41df59b8dd4fbd9877604e1a1a824c0c77323e9e6a3b81021929/images/aW1hZ2UvcG5n/9dd52733fd5e2a00f66061c65070f56e.png" 
            alt="SV Care Group" 
            className="h-24 md:h-40 lg:h-48 w-auto object-contain mb-8 origin-bottom" 
          />
          <h1 className="text-4xl md:text-7xl lg:text-[8vw] font-serif font-medium tracking-tight text-brand-dark text-center leading-[1.1]">
            Supporting Life <br />
            <span className="italic text-[#25a7df]">at Every Stage</span>
          </h1>
        </motion.div>

        {/* FOREGROUND LAYER - FAMILY - Fades away */}
        <motion.div 
          style={{ scale: familyScale, opacity: familyOpacity }}
          className="absolute bottom-0 z-10 w-full flex justify-center pointer-events-none"
        >
          <div className="relative w-[110%] md:w-[70%] max-w-5xl">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/84bd1a1ec4b169d3b45fb6d9222544ea571861834b2a41df59b8dd4fbd9877604e1a1a824c0c77323e9e6a3b81021929/images/aW1hZ2UvcG5n/b09ba0d349b15db0b86035500500de28.png" 
              alt="Family" 
              className="w-full h-auto object-cover mask-bottom-fade opacity-80"
              style={{ filter: "contrast(1.05) brightness(0.95)" }}
            />
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          style={{ opacity: useTransform(scrollYProgress, [0, 0.1], [1, 0]) }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-dark/40">Discover</span>
          <div className="w-[1px] h-10 bg-brand-dark/10 overflow-hidden">
            <motion.div 
              animate={{ y: ["-100%", "100%"] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="w-full h-full bg-brand-accent"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}